import { Color, Spacing } from '@persona-id/design-tokens';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { CasesFilterRelativeTimeSpan } from 'dashboard/models/table-view';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const DATE_TYPE_FILTER_OPTIONS: OptionType<'created_at' | 'resolved_at'>[] = [
  {
    label: 'Created at',
    value: 'created_at',
  },
  {
    label: 'Resolved at',
    value: 'resolved_at',
  },
];

const VIEW_INTERVAL_OPTIONS = [
  {
    label: 'No date range',
    value: 'none',
  },
  {
    label: 'Last 7 days',
    value: '7_days',
  },
  {
    label: 'Last 14 days',
    value: '14_days',
  },
  {
    label: 'Last 30 days',
    value: '30_days',
  },
  {
    label: 'This month',
    value: 'this_month',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];

const getIntervalFromDefaultOptions = (
  option: string,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): Pick<CasesFilterRelativeTimeSpan, 'interval' | 'unit'> => {
  switch (option) {
    case '7_days':
      return {
        interval: 7,
        unit: 'days',
      };
    case '14_days':
      return {
        interval: 14,
        unit: 'days',
      };
    case '30_days':
      return {
        interval: 30,
        unit: 'days',
      };
    case 'this_month':
      return {
        interval: 1,
        unit: 'months',
      };
  }
};

const StyledInput = styled(Input)`
  width: 15%;
`;

const CustomIntervalContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${Spacing[3]} ${Spacing[4]};

  justify-content: flex-start;
  gap: ${Spacing[3]};
  background: ${Color.CoreSlate100};
  border-radius: ${Spacing[1]};
`;

const getRelativeTimeSpan = (
  dateType: 'created_at' | 'resolved_at',
  interval: string,
  customDayInterval: number,
): CasesFilterRelativeTimeSpan => {
  switch (interval) {
    case 'none':
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return null;
    case 'custom':
      return { 'date-type': dateType, interval: customDayInterval, unit: 'days' };
    default:
      return {
        'date-type': dateType,
        ...getIntervalFromDefaultOptions(interval),
      };
  }
};

interface FormSaveTableViewProps {
  tableViewName: string;
  enableOverwrite: boolean;
  handleCancel: () => void;
  saveTableView: (name: string, interval: CasesFilterRelativeTimeSpan, saveAsNew: boolean) => void;
}

const FormSaveTableView = ({
  tableViewName,
  enableOverwrite,
  handleCancel,
  saveTableView,
}: FormSaveTableViewProps) => {
  const {
    control,
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<FormSaveTableViewData>({
    defaultValues: {
      name: tableViewName,
      dateType: 'created_at',
      interval: VIEW_INTERVAL_OPTIONS[0].value,
      customDayInterval: 14,
    },
    mode: 'onChange',
  });

  const handleOverwriteTableView = () => {
    const { name, dateType, interval, customDayInterval } = getValues();
    saveTableView(name, getRelativeTimeSpan(dateType, interval, customDayInterval), true);
  };

  const handleSaveNewTableView = ({
    name,
    dateType,
    interval,
    customDayInterval,
  }: FormSaveTableViewData) => {
    saveTableView(name, getRelativeTimeSpan(dateType, interval, customDayInterval), false);
  };

  return (
    <form onSubmit={handleSubmit(handleSaveNewTableView)}>
      <Label size='sm'>View name</Label>
      <Spacer height={1} />
      <Input
        {...register('name', {
          required: 'View must have a name.',
        })}
        disabled={isSubmitting}
      />
      {errors['name'] && <FormError>{errors['name'].message}</FormError>}
      <Spacer height={2} />
      <Label size='sm'>Date range</Label>
      <Spacer height={1} />
      <Controller
        control={control}
        name='dateType'
        render={({ field: { onChange, value } }) => (
          <ReactSelect
            onChange={onChange}
            value={value}
            options={DATE_TYPE_FILTER_OPTIONS}
            portal
          />
        )}
      />

      <Spacer height={1} />
      <Controller
        control={control}
        name='interval'
        render={({ field: { onChange, value } }) => (
          <ReactSelect onChange={onChange} value={value} options={VIEW_INTERVAL_OPTIONS} portal />
        )}
      />

      {watch('interval') === 'custom' && (
        <>
          <Spacer height={1} />
          <CustomIntervalContainer>
            Last
            <StyledInput {...register('customDayInterval')} size='sm' type='number' />
            {watch('customDayInterval') > 1 ? 'days' : 'day'}
          </CustomIntervalContainer>
        </>
      )}

      <Spacer height={2} />
      <HStack equal={true}>
        <Button
          id='configurable-cases-table__form-save-table-view--cancel'
          onClick={handleCancel}
          variant='basic'
        >
          Cancel
        </Button>
        {enableOverwrite && (
          <Button
            id='configurable-cases-table__form-save-table-view--overwrite'
            onClick={handleOverwriteTableView}
            variant='basic'
            disabled={isSubmitting || !isValid}
          >
            Overwrite
          </Button>
        )}

        <Button
          id='configurable-cases-table__form-save-table-view--save-as-new-view'
          type='submit'
          variant='primary'
          disabled={isSubmitting || !isValid}
        >
          Save as new view
        </Button>
      </HStack>
    </form>
  );
};

export interface FormSaveTableViewData {
  name: string;
  dateType: 'created_at' | 'resolved_at';
  interval: string;
  customDayInterval: number;
}

export default FormSaveTableView;
